import {
  AlignmentConnectionsComponent,
  ColliderShapeComponent,
  ComponentIds,
  Entity,
  HierarchyUtils,
  IsDraggedComponent,
  Polyhedron
} from '@inter-ikea-core/core';
import { SystemBase } from '@inter-ikea-core/core-toolbox';
import AdjustableStateComponent from '../components/AdjustableStateComponent';
import { CoreApi } from '../CoreApi';
import AdjustableComponent from '../components/AdjustableComponent';
import AdjustableHostComponent from '../components/AdjustableHostComponent';
import { CLOTH_RAIL_RIGHT_FRAME_CONNECTOR_KEY } from '../types';
import { isAssembly } from '../resolvers/common';

export default class RemoveClothRailBoundsOnFrameDragSystem extends SystemBase<IsDraggedComponent> {
  doingChangesOnComponent: number[] = [];
  systemsBeforeMe: string[] | null = null;
  systemsAfterMe: string[] | null = null;
  id: string = 'RemoveClothRailBoundsOnFrameDragSystem';
  coreApi: CoreApi;
  moveableChildPolyhedron: Polyhedron | undefined = undefined;
  MOVABLE_PART_KEY = 'moveable';

  constructor(coreApi: CoreApi) {
    super(ComponentIds.IS_DRAGGED_COMPONENT_ID);
    this.coreApi = coreApi;
  }

  // Fires when user starts dragging a frame
  protected onAddComponent(_entity: Entity, _newComponent: IsDraggedComponent) {
    super.onAddComponent(_entity, _newComponent);
    this.enableBounds(_entity, false);
  }

  // Fires when user stops dragging the frame and releases mouse button
  protected onRemoveComponent(_entity: Entity, _oldComponent: IsDraggedComponent) {
    this.enableBounds(_entity, true);
  }

  enableBounds = (entity: Entity, enable: boolean) => {
    if (isAssembly(entity)) {
      const allClothRails = this.coreApi.entities.getByComponent(AdjustableComponent.id);
      allClothRails.forEach(clothRail => this.toggleClothRailBounds(clothRail, enable));
    } else {
      if (entity.hasComponent(AdjustableComponent.id)) {
        this.toggleClothRailBounds(entity, enable);
      } else if (entity.hasComponent(AdjustableHostComponent.id)) {
        const frameStack = this.coreApi.entities.getStackedEntities(entity);

        const allAlignedClothRails = [...frameStack, entity]
          .flatMap(e => {
            return e
              .getComponent<AlignmentConnectionsComponent>(
                ComponentIds.ALIGNMENT_CONNECTIONS_COMPONENT_ID
              )
              ?.connections.filter(
                c => c.offeringConnector.key === CLOTH_RAIL_RIGHT_FRAME_CONNECTOR_KEY
              );
          })
          .filter(c => !!c);

        for (const clothRailConnection of allAlignedClothRails) {
          this.toggleClothRailBounds(clothRailConnection.offeringEntity, enable);
        }
      }
    }
  };

  toggleClothRailBounds = (entity: Entity, enable: boolean) => {
    HierarchyUtils.children(entity).forEach(child => {
      const state = child.getComponent<AdjustableStateComponent>(AdjustableStateComponent.id)
        ?.state;
      // Remove cloth rail bounds
      if (state === this.MOVABLE_PART_KEY) {
        if (enable) {
          this.moveableChildPolyhedron &&
            child.addComponent(new ColliderShapeComponent(this.moveableChildPolyhedron));
        } else {
          this.moveableChildPolyhedron = child.getComponent<ColliderShapeComponent>(
            ComponentIds.COLLIDER_SHAPE_COMPONENT_ID
          )?.polyhedron;
          child.removeComponent(ComponentIds.COLLIDER_SHAPE_COMPONENT_ID);
        }
      }
    });
  };
}
